"use client";

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Pencil } from "lucide-react";
import { Link } from "react-router-dom";

import {
  Button,
  ListCount,
  ListSurface,
  ListSurfaceBody,
  ListSurfaceFooter,
  ListSurfaceHeader,
  ListToolbar,
  ListToolbarGroup,
  SkeletonTableRow,
  WorkspacePage,
} from "@/components/ds";
import { tableHeaderSelectTriggerClass, ThemedSelect } from "@/components/ui-system/select";
import { getOtaCampaign, listOtaCampaignJobs } from "@/lib/api/endpoints/ota";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";
import { useWorkspaceChromeStore } from "@/store/workspace-chrome";

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
const JOB_STATUS_OPTIONS = [
  { value: "", label: "Any job status" },
  { value: "pending", label: "Pending" },
  { value: "offered", label: "Offered" },
  { value: "downloading", label: "Downloading" },
  { value: "installed", label: "Installed" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];

function formatDate(value?: string | null): string {
  return value ? new Date(value).toLocaleString() : "--";
}

function statusClass(status: string): string {
  if (status === "installed" || status === "active") return "border-success/25 bg-success/10 text-success";
  if (status === "failed" || status === "cancelled") return "border-error/25 bg-error/10 text-error";
  if (status === "paused" || status === "downloading" || status === "offered") return "border-warning/25 bg-warning/10 text-warning";
  return "border-card-border bg-surface-secondary text-muted";
}

export function OtaDetailPage({ campaignId }: { campaignId: string }) {
  const user = useAuthStore((s) => s.user);
  const setWorkspaceChrome = useWorkspaceChromeStore((s) => s.setWorkspaceChrome);
  const resetWorkspaceChrome = useWorkspaceChromeStore((s) => s.resetWorkspaceChrome);
  const { permissionMap } = useEffectivePermissions();
  const canEdit = hasModulePermission(user, permissionMap, "ota", "edit");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(10);

  useEffect(() => {
    setPage(0);
  }, [statusFilter, pageSize]);

  const campaignQuery = useQuery({
    queryKey: ["ota", "campaign", campaignId],
    queryFn: () => getOtaCampaign(campaignId),
    staleTime: 20_000,
  });

  const jobsQuery = useQuery({
    queryKey: ["ota", "campaign", campaignId, "jobs", statusFilter, page, pageSize],
    queryFn: () =>
      listOtaCampaignJobs(campaignId, {
        limit: pageSize,
        offset: page * pageSize,
        status: statusFilter || undefined,
      }),
    staleTime: 15_000,
    placeholderData: (previousData) => previousData,
  });

  const campaign = campaignQuery.data;
  const jobs = jobsQuery.data?.items ?? [];
  const total = jobsQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, page * pageSize + jobs.length);
  const tableSummary = useMemo(
    () => (total === 0 ? "No jobs" : `Showing ${from}-${to} of ${total}`),
    [from, to, total],
  );

  useEffect(() => {
    setWorkspaceChrome({ title: campaign ? `OTA ${campaign.version}` : "OTA" });
    return () => resetWorkspaceChrome();
  }, [campaign, resetWorkspaceChrome, setWorkspaceChrome]);

  return (
    <WorkspacePage className="max-w-[110rem]">
      <div className="space-y-ds-5">
        <div className="flex flex-wrap items-center justify-between gap-ds-3">
          <Link to="/ota" className="btn btn-secondary btn-sm !no-underline">
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back to OTA
          </Link>
          {canEdit ? (
            <Link to={`/ota/${campaignId}/edit`} className="btn btn-primary btn-sm !no-underline">
              <Pencil className="h-4 w-4" aria-hidden />
              Edit campaign
            </Link>
          ) : null}
        </div>

        <ListSurface>
          <ListSurfaceHeader
            title="Device jobs"
            description="Per-device OTA lifecycle for this campaign."
            meta={
              <>
                {campaign ? (
                  <span className="inline-flex flex-wrap items-center gap-ds-2">
                    <span className={`inline-flex rounded-full border px-ds-2 py-1 text-micro font-semibold uppercase ${statusClass(campaign.status)}`}>
                      {campaign.status}
                    </span>
                    <span>
                      {campaign.total_jobs} jobs · {campaign.installed_jobs} installed · {campaign.failed_jobs} failed
                    </span>
                  </span>
                ) : null}
                <ListCount total={total} noun={total === 1 ? "job" : "jobs"} />
              </>
            }
            controls={
              <ListToolbar>
                <ListToolbarGroup>
                  <ThemedSelect
                    value={statusFilter}
                    ariaLabel="Job status"
                    options={JOB_STATUS_OPTIONS}
                    onChange={setStatusFilter}
                    triggerClassName={tableHeaderSelectTriggerClass}
                    className="min-w-[180px]"
                  />
                </ListToolbarGroup>
              </ListToolbar>
            }
          />

          <ListSurfaceBody className="overflow-x-auto">
            <table className="min-w-full border-collapse text-left">
              <thead className="bg-surface-secondary">
                <tr>
                  <th className="border-b border-card-border px-ds-4 py-ds-3 text-micro font-semibold uppercase tracking-wider text-foreground">Device</th>
                  <th className="border-b border-card-border px-ds-4 py-ds-3 text-micro font-semibold uppercase tracking-wider text-foreground">Status</th>
                  <th className="border-b border-card-border px-ds-4 py-ds-3 text-micro font-semibold uppercase tracking-wider text-foreground">Current</th>
                  <th className="border-b border-card-border px-ds-4 py-ds-3 text-micro font-semibold uppercase tracking-wider text-foreground">Target</th>
                  <th className="border-b border-card-border px-ds-4 py-ds-3 text-micro font-semibold uppercase tracking-wider text-foreground">Last seen</th>
                  <th className="border-b border-card-border px-ds-4 py-ds-3 text-micro font-semibold uppercase tracking-wider text-foreground">Installed</th>
                  <th className="border-b border-card-border px-ds-4 py-ds-3 text-micro font-semibold uppercase tracking-wider text-foreground">Error</th>
                </tr>
              </thead>
              <tbody>
                {jobsQuery.isLoading || campaignQuery.isLoading ? (
                  <tr>
                    <td className="px-ds-4 py-ds-4" colSpan={7}>
                      {Array.from({ length: 6 }, (_, index) => <SkeletonTableRow key={index} cols={7} />)}
                    </td>
                  </tr>
                ) : jobsQuery.isError || campaignQuery.isError ? (
                  <tr>
                    <td className="px-ds-4 py-ds-6 text-caption text-error" colSpan={7}>
                      Failed to load OTA campaign.
                    </td>
                  </tr>
                ) : jobs.length === 0 ? (
                  <tr>
                    <td className="px-ds-4 py-ds-10 text-center text-caption text-muted" colSpan={7}>
                      No device jobs match the current filter.
                    </td>
                  </tr>
                ) : (
                  jobs.map((job) => (
                    <tr key={job.id} className="bg-surface-primary">
                      <td className="border-b border-card-border px-ds-4 py-ds-3">
                        <div className="font-semibold text-foreground">{job.device?.name || job.device?.device_uid || job.device_id}</div>
                      </td>
                      <td className="border-b border-card-border px-ds-4 py-ds-3">
                        <span className={`inline-flex rounded-full border px-ds-2 py-1 text-micro font-semibold uppercase ${statusClass(job.status)}`}>
                          {job.status}
                        </span>
                      </td>
                      <td className="border-b border-card-border px-ds-4 py-ds-3 text-caption text-muted">{job.current_version ?? "--"}</td>
                      <td className="border-b border-card-border px-ds-4 py-ds-3 text-caption text-foreground">{job.target_version ?? "--"}</td>
                      <td className="border-b border-card-border px-ds-4 py-ds-3 text-caption text-muted">{formatDate(job.last_seen_at)}</td>
                      <td className="border-b border-card-border px-ds-4 py-ds-3 text-caption text-muted">{formatDate(job.installed_at)}</td>
                      <td className="border-b border-card-border px-ds-4 py-ds-3 text-caption text-error">{job.error_message ?? "--"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ListSurfaceBody>

          <ListSurfaceFooter
            left={
              <div className="flex flex-wrap items-center gap-ds-3">
                <span>{tableSummary}</span>
                <label className="flex items-center gap-ds-2">
                  <span>Rows per page</span>
                  <ThemedSelect
                    value={String(pageSize)}
                    ariaLabel="Rows per page"
                    options={PAGE_SIZE_OPTIONS.map((size) => ({ value: String(size), label: String(size) }))}
                    onChange={(nextValue) => {
                      setPageSize(Number(nextValue) as (typeof PAGE_SIZE_OPTIONS)[number]);
                      setPage(0);
                    }}
                    triggerClassName={tableHeaderSelectTriggerClass}
                    className="min-w-[84px]"
                  />
                </label>
              </div>
            }
            right={
              <>
                <Button type="button" variant="secondary" size="sm" disabled={page === 0} onClick={() => setPage((current) => Math.max(0, current - 1))}>
                  Previous
                </Button>
                <span className="text-caption text-muted">Page {page + 1} / {pageCount}</span>
                <Button type="button" variant="secondary" size="sm" disabled={page + 1 >= pageCount} onClick={() => setPage((current) => current + 1)}>
                  Next
                </Button>
              </>
            }
          />
        </ListSurface>
      </div>
    </WorkspacePage>
  );
}
